import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import cx from 'classnames'
import { debounce } from 'lodash'
import { Modal, Button, Input } from 'antd'
import { ModalProps } from 'antd/lib/modal'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line import/no-extraneous-dependencies
import InfiniteScroll from 'react-infinite-scroller'

// assets
import { ReactComponent as CloseIcon } from '../../assets/icons/BaseIcons/x-icon.svg'
import { ReactComponent as PlusIcon } from '../../assets/icons/BaseIcons/plus-1-icon.svg'

// reducers
import { getTemplates, ITemplateItem } from '../../reducers/templates/templateActions'
import { RootState } from '../../reducers'

// utils
import { DEFAULT_NUMBER_OF_TEMPLATES, EDITING_TIME_MINUTES_LIMIT } from '../../utils/enums'

type Props = ModalProps & {
	isVisible: boolean
	onCancel: Function | React.MouseEvent<HTMLElement, MouseEvent>
	btnPushed: Function
	createFormFromTemplate: Function
}

const CreateFormModal: FC<Props> = (props) => {
	const { isVisible, onCancel, width, btnPushed, createFormFromTemplate } = props
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const templates = useSelector((state: RootState) => state.template.templates)
	const [count, setCount] = useState(DEFAULT_NUMBER_OF_TEMPLATES)
	const [search, setSearch] = useState<string>('')

	const fetchData = () => {
		if (!templates.isLoading) {
			setCount(count + DEFAULT_NUMBER_OF_TEMPLATES)
		}
	}

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCount(DEFAULT_NUMBER_OF_TEMPLATES)
		setSearch(event?.target?.defaultValue)
	}

	useEffect(() => {
		dispatch(
			getTemplates(1, count, {
				search
			})
		)
	}, [dispatch, count, search])

	const check = (): boolean => {
		if ((templates?.originalData?.length || 0) < (templates.pagination?.totalCount || 0)) {
			return true
		}
		return false
	}

	const renderTitle = (template: ITemplateItem, index: number) => {
		const time = Math.abs(dayjs(template?.updatedAt || 0).diff(dayjs(), 'minutes'))
		const checkTime = time >= EDITING_TIME_MINUTES_LIMIT
		const editClass = cx({
			'text-gray-400': !checkTime,
			'text-gray-700': checkTime
		})

		return (
			<div
				onClick={() => {
					if (checkTime) {
						createFormFromTemplate(template)
					}
				}}
				className={`h-8 flex template-item items-center ${editClass}`}
				key={`key-${index}`}
			>
				<p className={`m-0 text-sm w-12 text-right ${editClass}`}>{template?.id}</p>{' '}
				<p id={'template-list-name'} className={'m-0 ml-6 text-sm overflow-hidden overflow-ellipsis whitespace-nowrap'}>
					{template?.name}
				</p>
			</div>
		)
	}

	return (
		<Modal
			className={'creat-form-modal'}
			closeIcon={<CloseIcon width={18} height={18} />}
			title={t('loc:Create form')}
			centered
			open={isVisible}
			width={width}
			onCancel={onCancel}
			footer={[]}
		>
			<Button className={'h-10 w-full unfilled-btn'} key='create' onClick={() => btnPushed()}>
				<PlusIcon />
				{t('loc:New blank form')}
			</Button>
			<div className={'mt-6 h-10'}>
				<h3 className={'text-lg'}>{t('loc:Templates')}</h3>
			</div>
			<Input.Search
				className='list-search-input inset-0 w-full searchInput'
				placeholder={t('loc:Search template...') as string}
				prefix={<SearchOutlined />}
				onChange={debounce(handleChange, 600)}
				loading={templates?.isLoading}
			/>
			<div
				style={{
					height: 224,
					overflow: 'auto'
				}}
			>
				<InfiniteScroll
					useWindow={false}
					loadMore={fetchData}
					hasMore={check()}
					loader={<h4 className={'loader text-gray-700'}>{`${t('loc:Loading')}...`}</h4>}
				>
					{/* eslint-disable-next-line react/jsx-no-useless-fragment */}
					<>{templates?.originalData?.map((template, index) => renderTitle(template, index))}</>
				</InfiniteScroll>
			</div>
		</Modal>
	)
}

export default CreateFormModal
